import React, { useMemo } from "react";
import { Building2, FolderOpen, Search } from "lucide-react";
import { useAdminUsers } from "../../../hooks/useAdminUsage";
import { mapUserListItemToWorkspaceProfileRow } from "../../../utils/admin/mapUserToWorkspaceProfileRow";
import { DataTableOrganism } from "@/components/ui/organisms";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
} from "@/components/ui/molecules";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import { workspaceProfileTableColumns } from ".";
import { getTotalWorkspaces } from "./workspace-profiles.utils";

interface WorkspaceProfilesListProps {
  isAdmin: boolean;
  isLoading: boolean;
}

export const WorkspaceProfilesList: React.FC<WorkspaceProfilesListProps> = ({
  isAdmin,
  isLoading,
}) => {
  const { data: users = [], isLoading: isLoadingUsers, error } = useAdminUsers({
    enabled: isAdmin && !isLoading,
  });

  const tableRows = useMemo(
    () => users.map(mapUserListItemToWorkspaceProfileRow),
    [users]
  );
  const totalWorkspaces = useMemo(() => getTotalWorkspaces(users), [users]);

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<FolderOpen />}
          title="No workspace profiles yet"
          description="Profiles appear here after users complete plan activation and onboarding."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<Search />}
        title="No matching profiles"
        description="Try adjusting your search by name, email, or company."
      />
    );
  }, [tableRows.length]);

  if (!isAdmin) {
    return null;
  }

  const status = isLoadingUsers ? "loading" : error ? "error" : "ready";
  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : "";

  if (status === "loading") {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading workspace profiles…"
      />
    );
  }

  if (status === "error") {
    return (
      <SummarizedListSectionTemplate
        status="error"
        errorTitle="Error loading workspace profiles"
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <SummarizedListSectionTemplate
      status="ready"
      summary={
        <SummaryStatCard
          label="Total workspaces"
          value={totalWorkspaces}
          icon={<Building2 />}
        />
      }
      primary={
        <DataTableOrganism
          columns={workspaceProfileTableColumns}
          data={tableRows}
          pageSize={10}
          enableSelection
          enableSearch
          enableColumnVisibility
          columnVisibilityLabel="Column visibility"
          toolbarTitle={
            <AccentBarHeading>Workspace Profiles</AccentBarHeading>
          }
          searchPlaceholder="Search by name, email, company…"
          searchKeys={["fullName", "email", "company"]}
          paginationItemLabel="profiles"
          dataTableProps={{
            emptyState,
          }}
        />
      }
    />
  );
};
